import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getGroupedMenus, ROLE_DEFAULT_OPEN_GROUPS } from '../constants/menus';
import { colors, radius, shadows } from '../styles/theme';
import { ThemeToggle } from './analytics/ThemeToggle';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const HR_BILDIRIM_POLL_MS = 5 * 60 * 1000; // 5 dakika

// İK menüsü için bildirim rozeti. Görsel kural:
//   sayı > 0 → küçük kırmızı daire + sayı ("99+" cap)
//   sayı == 0 → rozet gösterilmez (null döner)
const BildirimRozeti = ({ sayi, compact }) => {
    if (!sayi || sayi <= 0) return null;
    const gosterim = sayi > 99 ? '99+' : String(sayi);
    return (
        <span style={{
            background: '#e53935',
            color: '#fff',
            fontSize: compact ? '9px' : '10px',
            fontWeight: 700,
            padding: compact ? '1px 5px' : '2px 6px',
            borderRadius: '10px',
            minWidth: compact ? '16px' : '18px',
            textAlign: 'center',
            lineHeight: 1.2,
            marginLeft: 'auto',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
        }}>
            {gosterim}
        </span>
    );
};

const Sidebar = ({ user, sidebarOpen, setSidebarOpen, logout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const groups = getGroupedMenus(user.role, user.effectivePermissions);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // İK bildirim sayısı (sadece yonetim/ik/admin rolleri için çekilir)
    const hrBadgeGorunsun = user && (user.role === 'ik' || user.role === 'yonetim' || user.role === 'admin');
    const [hrBildirimSayi, setHrBildirimSayi] = useState(0);

    useEffect(() => {
        if (!hrBadgeGorunsun) return undefined;
        let aktif = true;
        const yukle = () => {
            fetch(`${API_URL}/api/hr/bildirimler/ozet`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            })
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    if (!aktif || !d) return;
                    // Aksiyon beklenen sayı = bekleyen (gecikmiş + bu hafta + uzak hepsi)
                    setHrBildirimSayi(Number(d.bekleyen || 0));
                })
                .catch(() => {});
        };
        yukle();
        const iv = setInterval(yukle, HR_BILDIRIM_POLL_MS);
        return () => { aktif = false; clearInterval(iv); };
    }, [hrBadgeGorunsun]);

    // Verilen menü item'ı için rozet sayısı (şimdilik sadece 'ik')
    const itemBadgeSayi = (item) => {
        if (item.id === 'ik' && hrBadgeGorunsun) return hrBildirimSayi;
        return 0;
    };

    const handleResize = useCallback(() => {
        const mobile = window.innerWidth < 768;
        setIsMobile(mobile);
        if (!mobile) setMobileMenuOpen(false);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    useEffect(() => {
        if (isMobile) setMobileMenuOpen(false);
    }, [location.pathname, isMobile]);

    const currentGroup = groups.find(g => g.items.some(i => i.route === location.pathname));
    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        const defaults = ROLE_DEFAULT_OPEN_GROUPS[user.role] || ['Ana Sayfa'];
        defaults.forEach(g => { initial[g] = true; });
        if (currentGroup) initial[currentGroup.group] = true;
        return initial;
    });

    const toggleGroup = (group) => {
        setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
    };

    const isActive = (route) => location.pathname === route;

    // User initials avatar
    const initials = (user.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    const hamburgerButton = isMobile ? (
        <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
                position: 'fixed', top: '12px', left: '12px', zIndex: 201,
                background: colors.secondary, color: 'white', border: 'none',
                borderRadius: radius.md, padding: '10px 14px', fontSize: '18px',
                cursor: 'pointer', boxShadow: shadows.lg,
                lineHeight: 1, fontFamily: 'inherit',
            }}
            aria-label="Menu"
        >
            {mobileMenuOpen ? '\u2715' : '\u2630'}
        </button>
    ) : null;

    const mobileOverlay = isMobile && mobileMenuOpen ? (
        <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 149,
                backdropFilter: 'blur(2px)',
            }}
        />
    ) : null;

    const sidebarVisible = isMobile ? mobileMenuOpen : true;
    const effectiveSidebarOpen = isMobile ? true : sidebarOpen;

    return (
        <>
        {hamburgerButton}
        {mobileOverlay}
        <div style={{
            width: effectiveSidebarOpen ? '260px' : '64px',
            background: colors.secondary,
            color: 'white',
            height: '100vh',
            transition: isMobile ? 'transform 0.3s cubic-bezier(0.4,0,0.2,1)' : 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: isMobile ? 150 : 100,
            display: 'flex',
            flexDirection: 'column',
            transform: isMobile && !sidebarVisible ? 'translateX(-100%)' : 'translateX(0)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
            {/* Logo */}
            <div style={{
                padding: effectiveSidebarOpen ? '20px 20px 16px' : '20px 12px 16px',
                borderBottom: `1px solid ${colors.secondaryLight}`,
                display: 'flex', alignItems: 'center',
                justifyContent: effectiveSidebarOpen ? 'space-between' : 'center',
                flexShrink: 0,
            }}>
                {effectiveSidebarOpen && (
                    <div
                        onClick={() => navigate('/dashboard')}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                        <div style={{
                            width: '32px', height: '32px', borderRadius: radius.sm,
                            background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '15px', fontWeight: '800', color: '#fff', letterSpacing: '-0.02em',
                        }}>
                            T
                        </div>
                        <span style={{ fontSize: '16px', fontWeight: '700', letterSpacing: '-0.01em' }}>TORUNA</span>
                    </div>
                )}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    style={{
                        background: colors.secondaryLight, border: 'none', color: 'rgba(255,255,255,0.6)',
                        padding: '6px 8px', borderRadius: radius.sm, cursor: 'pointer',
                        fontSize: '12px', lineHeight: 1, transition: 'color 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#fff'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                >
                    {sidebarOpen ? '\u25C0' : '\u25B6'}
                </button>
            </div>

            {/* User Info */}
            {effectiveSidebarOpen && (
                <div style={{
                    padding: '16px 20px',
                    borderBottom: `1px solid ${colors.secondaryLight}`,
                    display: 'flex', alignItems: 'center', gap: '12px',
                    flexShrink: 0,
                }}>
                    <div style={{
                        width: '36px', height: '36px', borderRadius: radius.full,
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '13px', fontWeight: '700', color: '#fff',
                        flexShrink: 0,
                    }}>
                        {initials}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>{user.department}</div>
                    </div>
                </div>
            )}

            {/* Menu */}
            <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
                {groups.map((g) => {
                    const isOpen = openGroups[g.group];
                    const hasActiveItem = g.items.some(i => isActive(i.route));

                    if (!effectiveSidebarOpen) {
                        return (
                            <div key={g.group}>
                                {g.items.map(item => {
                                    const active = isActive(item.route);
                                    const badgeSayi = itemBadgeSayi(item);
                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => navigate(item.route)}
                                            title={item.label + (badgeSayi > 0 ? ` (${badgeSayi} bekleyen)` : '')}
                                            style={{
                                                width: '100%', padding: '12px', border: 'none',
                                                background: active ? colors.primaryGhost : 'transparent',
                                                borderLeft: active ? `3px solid ${colors.primary}` : '3px solid transparent',
                                                color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                                                textAlign: 'center', cursor: 'pointer', fontSize: '16px',
                                                transition: 'all 0.15s', position: 'relative',
                                            }}
                                        >
                                            {item.icon}
                                            {badgeSayi > 0 && (
                                                <span style={{
                                                    position: 'absolute', top: 6, right: 6,
                                                    background: '#e53935', color: '#fff',
                                                    fontSize: '9px', fontWeight: 700,
                                                    padding: '1px 4px', borderRadius: 8,
                                                    minWidth: 14, lineHeight: 1.2,
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                }}>
                                                    {badgeSayi > 99 ? '99+' : badgeSayi}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    }

                    // Single-item group
                    if (g.items.length === 1) {
                        const item = g.items[0];
                        const active = isActive(item.route);
                        const badgeSayi = itemBadgeSayi(item);
                        return (
                            <button
                                key={g.group}
                                onClick={() => navigate(item.route)}
                                style={{
                                    width: '100%', padding: '10px 16px 10px 20px', border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    background: active ? 'rgba(198,40,40,0.12)' : 'transparent',
                                    borderLeft: active ? `3px solid ${colors.primary}` : '3px solid transparent',
                                    color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                                    fontSize: '13px', fontWeight: active ? 600 : 500,
                                    transition: 'all 0.15s', marginBottom: '1px',
                                    fontFamily: 'inherit',
                                }}
                                onMouseOver={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                onMouseOut={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span style={{ fontSize: '15px', opacity: active ? 1 : 0.7 }}>{item.icon || g.icon}</span>
                                <span>{item.label}</span>
                                <BildirimRozeti sayi={badgeSayi} />
                            </button>
                        );
                    }

                    return (
                        <div key={g.group} style={{ marginBottom: '2px' }}>
                            {/* Group header */}
                            <button
                                onClick={() => toggleGroup(g.group)}
                                style={{
                                    width: '100%', padding: '8px 16px 8px 20px', border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: 'transparent',
                                    color: hasActiveItem ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)',
                                    fontSize: '11px', fontWeight: 600,
                                    letterSpacing: '0.06em', textTransform: 'uppercase',
                                    transition: 'color 0.2s', fontFamily: 'inherit',
                                    marginTop: '12px',
                                }}
                                onMouseOver={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                                onMouseOut={(e) => e.currentTarget.style.color = hasActiveItem ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)'}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '13px' }}>{g.icon}</span>
                                    {g.group}
                                </span>
                                <span style={{
                                    fontSize: '9px', transition: 'transform 0.2s',
                                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                    opacity: 0.5,
                                }}>&#9654;</span>
                            </button>

                            {/* Group items */}
                            {isOpen && (
                                <div>
                                    {g.items.map(item => {
                                        const active = isActive(item.route);
                                        const badgeSayi = itemBadgeSayi(item);
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => navigate(item.route)}
                                                style={{
                                                    width: '100%', padding: '9px 20px 9px 44px',
                                                    background: active ? 'rgba(198,40,40,0.12)' : 'transparent',
                                                    borderLeft: active ? `3px solid ${colors.primary}` : '3px solid transparent',
                                                    border: 'none',
                                                    borderLeftWidth: '3px', borderLeftStyle: 'solid',
                                                    borderLeftColor: active ? colors.primary : 'transparent',
                                                    color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                                                    textAlign: 'left', cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center', gap: '8px',
                                                    fontSize: '13px', fontWeight: active ? 500 : 400,
                                                    transition: 'all 0.15s', fontFamily: 'inherit',
                                                }}
                                                onMouseOver={(e) => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; } }}
                                                onMouseOut={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; } }}
                                            >
                                                <span style={{ fontSize: '14px', opacity: active ? 1 : 0.6 }}>{item.icon}</span>
                                                <span>{item.label}</span>
                                                <BildirimRozeti sayi={badgeSayi} compact />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* Logout */}
            <div style={{ flexShrink: 0, borderTop: `1px solid ${colors.secondaryLight}` }}>
                <ThemeToggle expanded={sidebarOpen} />
                <button
                    onClick={logout}
                    style={{
                        width: '100%', padding: '14px 20px', background: 'transparent', border: 'none',
                        color: 'rgba(255,255,255,0.5)', textAlign: effectiveSidebarOpen ? 'left' : 'center',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        fontSize: '13px', fontFamily: 'inherit', transition: 'color 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                >
                    <span style={{ fontSize: '15px' }}>&#x2190;</span>
                    {effectiveSidebarOpen && <span>Çıkış Yap</span>}
                </button>
            </div>
        </div>
        </>
    );
};

export default Sidebar;
